import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bike, Bus, Car, CarFront, FileText, History, Link2, Truck } from 'lucide-react';

import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailField } from '@/components/ui/entity-detail-field';
import { EntityDetailsHeader } from '@/components/ui/entity-details-header';
import { StatusBadge } from '@/components/ui/status-badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import type { VehicleViewModel } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { getVehicleMakeLogoUrl } from '@/hooks/useVehicleMakesModels';

import { VEHICLE_QUERY } from '../vehicles.gql.tsx';
import { VehicleContracts } from './vehicle-contracts';
import { VehicleReferencesTab } from './vehicle-references-tab';
import { VehicleParkingSessions } from './vehicle-parking-sessions';

interface VehicleDetailsProps {
	vehicleId: string;
	activeTab?: string;
	onTabChange?: (tab: string) => void;
	hideTabsList?: boolean;
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

const resolveVehicleIcon = (vehicleTypeName?: string | null) => {
	const normalizedType = vehicleTypeName?.toLowerCase() ?? '';

	if (normalizedType.includes('motor')) {
		return Bike;
	}

	if (normalizedType.includes('truck') || normalizedType.includes('lorry')) {
		return Truck;
	}

	if (normalizedType.includes('bus')) {
		return Bus;
	}

	return CarFront;
};

export const VehicleDetails = ({
	vehicleId,
	activeTab: externalActiveTab,
	onTabChange: externalOnTabChange,
	hideTabsList,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: VehicleDetailsProps) => {
	const { t } = useTranslation('common');
	const { t: tV } = useTranslation('vehicles');
	const [internalActiveTab, setInternalActiveTab] = useState('details');
	const [logoError, setLogoError] = useState(false);
	const [hasOpenedReferences, setHasOpenedReferences] = useState(
		externalActiveTab === 'references'
	);

	// Use external control if provided, otherwise use internal state
	const activeTab = externalActiveTab ?? internalActiveTab;
	const setActiveTab = externalOnTabChange ?? setInternalActiveTab;

	const handleTabChange = (tab: string) => {
		if (tab === 'references' && !hasOpenedReferences) {
			setHasOpenedReferences(true);
		}
		setActiveTab(tab);
	};

	useEffect(() => {
		if (activeTab === 'references' && !hasOpenedReferences) {
			setHasOpenedReferences(true);
		}
	}, [activeTab, hasOpenedReferences]);

	const { data, isLoading, isError } = useOne<VehicleViewModel>({
		resource: 'vehicles',
		id: vehicleId,
		meta: {
			gqlQuery: VEHICLE_QUERY,
			dataPath: 'vehicle'
		}
	});

	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 300,
		showInitialLoading: false
	});

	const vehicle = data?.data;
	const logoUrl = getVehicleMakeLogoUrl(vehicle?.make);

	useEffect(() => {
		setLogoError(false);
	}, [logoUrl]);

	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-32 h-4 rounded animate-pulse bg-muted" />
				<div className="w-20 h-4 rounded animate-pulse bg-muted" />
				<div className="space-y-2">
					<div className="w-full h-3 rounded animate-pulse bg-muted" />
					<div className="w-3/4 h-3 rounded animate-pulse bg-muted" />
				</div>
			</div>
		);
	}

	if (!isLoading && (isError || !vehicle)) {
		return (
			<div className="py-8 text-sm text-center text-muted-foreground">
				{tV('details.loadError')}
			</div>
		);
	}

	if (!vehicle) {
		return null;
	}

	const hasVehicleInformation = Boolean(
		(vehicle.make ?? '').trim() ||
			(vehicle.model ?? '').trim() ||
			(vehicle.color ?? '').trim()
	);

	const customerDisplayName = vehicle.customer?.displayName ?? null;

	const displayName = vehicle.plateNumber || tV('details.unnamedVehicle');
	const entityTypeName = vehicle.isActive ? t('activeVehicle') : t('inactiveVehicle');
	const VehicleHeaderIcon = resolveVehicleIcon(vehicle.vehicleType?.name);

	return (
		<div className="flex flex-col h-full">
			{/* Header - shown when in modal mode */}
			{showHeader && (
				<EntityDetailsHeader
					icon={
						<div
							className={`flex items-center justify-center w-14 h-14 rounded-full bg-transparent flex-shrink-0 overflow-hidden ${
								logoUrl && !logoError
									? ''
									: 'border-2 border-gray-400 dark:border-gray-500'
							}`}
						>
							{logoUrl && !logoError ? (
								<img
									src={logoUrl}
									alt={vehicle.make ? `${vehicle.make} logo` : 'Vehicle logo'}
									className="h-full w-full object-cover"
									onError={() => setLogoError(true)}
								/>
							) : (
								<VehicleHeaderIcon className="w-7 h-7 text-gray-600 dark:text-gray-300" />
							)}
						</div>
					}
					name={displayName}
					statusBadge={<StatusBadge isActive={vehicle.isActive} />}
					entityTypeName={entityTypeName}
					entityId={vehicleId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4 border-b border-gray-200 dark:border-slate-700"
				/>
			)}

			<Tabs
				value={activeTab}
				onValueChange={handleTabChange}
				className="flex flex-col h-full min-h-0"
			>
				{/* Tab Bar - fixed height at top */}
				{!hideTabsList && (
					<div className="flex-shrink-0 pb-2">
						<TabsList className="gap-2 bg-transparent p-0">
							<TabsTrigger
								value="details"
								className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
							>
								<FileText className="w-4 h-4" />
								<span>{tV('tabs.details')}</span>
							</TabsTrigger>
							<TabsTrigger
								value="parkingSessions"
								className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
							>
								<Car className="w-4 h-4" />
								<span>{tV('tabs.parkingSessions')}</span>
							</TabsTrigger>
							<TabsTrigger
								value="contracts"
								className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
							>
								<FileText className="w-4 h-4" />
								<span>{t('tabs.contracts')}</span>
							</TabsTrigger>
							<TabsTrigger
								value="references"
								className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
							>
								<Link2 className="w-4 h-4" />
								<span>{t('tabs.references')}</span>
							</TabsTrigger>
							<TabsTrigger
								value="audit"
								align="right"
								className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
							>
								<History className="w-4 h-4" />
								<span>{t('tabs.audit')}</span>
							</TabsTrigger>
						</TabsList>
					</div>
				)}

				{/* Tab Content - scrollable area with min-height for stable modal sizing */}
				<div className="flex-1 min-h-[350px] overflow-y-auto overflow-x-hidden">
					<TabsContent value="details" className="mt-0">
					<div className="space-y-6 text-base">
						{vehicle.customer && (
							<DetailSection
								title={tV('details.ownerSection')}
							>
								<DetailFieldsGrid>
									<EntityDetailField
										label={tV('details.customer')}
										value={customerDisplayName}
										entityId={
											vehicle.customer.customerId
										}
										entityType="customers"
									/>
									<DetailField
										label={tV('details.customerType')}
										value={
											vehicle.customer.isCompany
												? tV('details.company')
												: tV('details.individual')
										}
									/>
									{vehicle.customer.email && (
										<DetailField
											label={tV(
												'details.customerEmail'
											)}
											value={vehicle.customer.email}
										/>
									)}
								</DetailFieldsGrid>
							</DetailSection>
						)}

						{hasVehicleInformation && (
							<DetailSection
								title={t('sections.vehicle_information')}
							>
								<DetailFieldsGrid>
									<DetailField
										label={tV('details.make')}
										value={vehicle.make}
									/>
									<DetailField
										label={tV('details.model')}
										value={vehicle.model}
									/>
									<DetailField
										label={tV('details.color')}
										value={vehicle.color}
									/>
								</DetailFieldsGrid>
							</DetailSection>
						)}
					</div>
					</TabsContent>

					<TabsContent value="parkingSessions" className="mt-0">
						<VehicleParkingSessions
							vehicleId={vehicleId}
							isActive={activeTab === 'parkingSessions'}
						/>
					</TabsContent>

					<TabsContent value="contracts" className="mt-0">
						<VehicleContracts
							vehicleId={vehicleId}
							isActive={activeTab === 'contracts'}
						/>
					</TabsContent>

					<TabsContent value="references" className="mt-0">
						{hasOpenedReferences && (
							<VehicleReferencesTab vehicleId={vehicleId} />
						)}
					</TabsContent>

					<TabsContent value="audit" className="mt-0">
						<AuditTab
							data={{
								createdAt: vehicle.createdAt,
								activatedAt: vehicle.activatedAt,
								deactivatedAt: vehicle.deactivatedAt
							}}
						/>
					</TabsContent>
				</div>
			</Tabs>
		</div>
	);
};
